import React, { useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { siteData } from '../../data/mock';
import { Check, CheckCircle, Shield } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fbTrack = (eventName, params) => {
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('track', eventName, params);
  }
};

const Hero = () => {
  const { hero } = siteData;
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    consent: false,
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const hasTrackedInteraction = useRef(false);
  const fireFormInteraction = useCallback(() => {
    if (!hasTrackedInteraction.current) {
      hasTrackedInteraction.current = true;
      fbTrack('ViewContent', { content_name: 'Legal Consultation Form Interaction' });
    }
  }, []);

  const validate = (name, value) => {
    switch (name) {
      case 'first_name':
      case 'last_name':
        return value.length >= 2;
      case 'email':
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
      case 'phone':
        return value.length >= 6;
      default:
        return true;
    }
  };

  const handleFocus = () => fireFormInteraction();

  const handleChange = (e) => {
    fireFormInteraction();
    const { name, value, type, checked } = e.target;
    const val = type === 'checkbox' ? checked : value;
    setFormData((prev) => ({ ...prev, [name]: val }));
    if (type !== 'checkbox') {
      setTouched((prev) => ({ ...prev, [name]: true }));
      setErrors((prev) => ({ ...prev, [name]: !validate(name, value) }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');

    const newErrors = {};
    hero.formFields.forEach((field) => {
      if (!validate(field.name, formData[field.name] || '')) {
        newErrors[field.name] = true;
      }
    });

    if (!formData.consent) newErrors.consent = true;

    setErrors(newErrors);
    setTouched({ first_name: true, last_name: true, email: true, phone: true });

    if (Object.keys(newErrors).length === 0) {
      setSubmitting(true);
      try {
        await axios.post(`${API}/leads`, {
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email,
          phone: formData.phone,
        });

        fbTrack('Lead', {
          content_name: 'Lex Recupero Consulenza Legale',
          content_category: 'Legal Services',
        });

        setSubmitted(true);
      } catch (err) {
        if (err.response?.status === 409) {
          setSubmitError('Esiste già una richiesta con lo stesso numero di telefono.');
        } else if (err.response?.status === 400) {
          setSubmitError('Il numero di telefono è obbligatorio.');
        } else {
          setSubmitError('Si è verificato un errore. Riprova più tardi.');
        }
      } finally {
        setSubmitting(false);
      }
    }
  };

  if (submitted) {
    return (
      <section
        className="relative pt-[70px] overflow-hidden"
        style={{
          background:
            'linear-gradient(160deg, #0a1628 0%, #0c2d6b 20%, #1256b9 42%, #1a6ee0 55%, #1562cc 68%, #0c3577 85%, #0a1628 100%)',
        }}
      >
        <div className="absolute inset-0 pointer-events-none">
          <div
            className="absolute top-[15%] left-[40%] w-[900px] h-[700px] rounded-full opacity-40"
            style={{
              background:
                'radial-gradient(ellipse at center, rgba(56,152,255,0.55) 0%, rgba(30,100,220,0.25) 35%, transparent 70%)',
            }}
          />
        </div>
        <div className="relative z-10 max-w-[700px] mx-auto px-4 sm:px-6 py-20 md:py-32 text-center">
          <div className="bg-[#0b1a3d]/80 backdrop-blur-md rounded-2xl p-8 md:p-12 border border-white/[0.08] shadow-2xl">
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={44} className="text-green-400" />
            </div>
            <h2 className="text-white text-[28px] md:text-[36px] font-bold mb-4 leading-tight">
              Richiesta ricevuta con successo
            </h2>
            <p className="text-white/70 text-base md:text-lg leading-relaxed mb-6">
              Il nostro studio legale esaminerà la tua richiesta e ti contatterà entro 1-2 giorni lavorativi per la valutazione preliminare gratuita e riservata del caso.
            </p>
            <p className="text-white/45 text-sm leading-relaxed mb-6">
              Ricorda: la valutazione iniziale non comporta alcun impegno. Ogni caso viene esaminato individualmente e in modo riservato.
            </p>
            <div className="flex items-center justify-center gap-2 text-white/40 text-[12px]">
              <Shield size={14} />
              <span>I tuoi dati sono protetti e trattati ai sensi del GDPR</span>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      className="relative pt-[70px] overflow-hidden"
      style={{
        background:
          'linear-gradient(160deg, #0a1628 0%, #0c2d6b 20%, #1256b9 42%, #1a6ee0 55%, #1562cc 68%, #0c3577 85%, #0a1628 100%)',
      }}
    >
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute top-[15%] left-[40%] w-[900px] h-[700px] rounded-full opacity-40"
          style={{
            background:
              'radial-gradient(ellipse at center, rgba(56,152,255,0.55) 0%, rgba(30,100,220,0.25) 35%, transparent 70%)',
          }}
        />
        <div
          className="absolute top-[5%] left-[10%] w-[600px] h-[500px] rounded-full opacity-30"
          style={{
            background:
              'radial-gradient(ellipse at center, rgba(80,170,255,0.45) 0%, transparent 65%)',
          }}
        />
        <div
          className="absolute bottom-[0%] left-[20%] w-[1100px] h-[400px] rounded-full opacity-25"
          style={{
            background:
              'radial-gradient(ellipse at center, rgba(50,140,255,0.5) 0%, transparent 60%)',
          }}
        />
      </div>

      <svg
        className="absolute bottom-0 left-0 w-full h-[320px] pointer-events-none opacity-30"
        preserveAspectRatio="none"
        viewBox="0 0 1440 320"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d="M0,224 C180,280 360,180 540,200 C720,220 900,300 1080,260 C1260,220 1380,180 1440,190 L1440,320 L0,320 Z" fill="url(#wg1)" />
        <path d="M0,260 C200,220 400,300 600,270 C800,240 1000,280 1200,250 C1320,235 1400,260 1440,270 L1440,320 L0,320 Z" fill="url(#wg2)" />
        <defs>
          <linearGradient id="wg1" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="rgba(30,120,255,0.3)" />
            <stop offset="50%" stopColor="rgba(80,180,255,0.2)" />
            <stop offset="100%" stopColor="rgba(30,120,255,0.3)" />
          </linearGradient>
          <linearGradient id="wg2" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="rgba(20,90,220,0.25)" />
            <stop offset="50%" stopColor="rgba(60,150,255,0.15)" />
            <stop offset="100%" stopColor="rgba(20,90,220,0.25)" />
          </linearGradient>
        </defs>
      </svg>

      <svg
        className="absolute bottom-[80px] left-0 w-full h-[200px] pointer-events-none opacity-20"
        preserveAspectRatio="none"
        viewBox="0 0 1440 200"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d="M0,120 C240,60 480,160 720,100 C960,40 1200,140 1440,80" fill="none" stroke="rgba(120,200,255,0.6)" strokeWidth="1.5" />
        <path d="M0,140 C300,80 600,180 900,110 C1100,70 1300,150 1440,100" fill="none" stroke="rgba(100,180,255,0.4)" strokeWidth="1" />
      </svg>

      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-[12%] left-[8%] w-2 h-2 bg-blue-300/50 rounded-full blur-[1px]" />
        <div className="absolute top-[25%] left-[18%] w-1.5 h-1.5 bg-sky-300/40 rounded-full blur-[1px]" />
        <div className="absolute top-[8%] left-[55%] w-2.5 h-2.5 bg-blue-200/35 rounded-full blur-[2px]" />
        <div className="absolute top-[35%] left-[75%] w-2 h-2 bg-cyan-300/40 rounded-full blur-[1px]" />
        <div className="absolute top-[60%] left-[85%] w-1.5 h-1.5 bg-blue-300/30 rounded-full blur-[1px]" />
        <div className="absolute top-[50%] left-[5%] w-3 h-3 bg-sky-200/25 rounded-full blur-[3px]" />
        <div className="absolute top-[22%] left-[70%] w-1 h-1 bg-white/30 rounded-full" />
        <div
          className="absolute top-[30%] right-[15%] w-16 h-16 rounded-full opacity-15"
          style={{ background: 'radial-gradient(circle, rgba(120,200,255,0.8) 0%, transparent 70%)' }}
        />
      </div>

      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-[10%] right-[20%] w-[200px] h-[200px] rounded-full border border-white/[0.06]" />
        <div className="absolute top-[10%] right-[20%] w-[260px] h-[260px] rounded-full border border-white/[0.04] -translate-x-[30px] -translate-y-[30px]" />
        <div className="absolute bottom-[15%] left-[5%] w-[180px] h-[180px] rounded-full border border-white/[0.05]" />
      </div>

      <div className="relative z-10 max-w-[1200px] mx-auto px-4 sm:px-6 py-10 md:py-16 lg:py-20">
        <div className="flex flex-col lg:flex-row items-center gap-6 lg:gap-12">
          <div className="w-full lg:w-5/12 text-center">
            <h1 className="text-white text-[38px] md:text-[56px] font-bold mb-2 md:mb-3 leading-tight drop-shadow-lg">
              {hero.title}
            </h1>
            <h2 className="text-white/80 text-base md:text-xl font-normal mb-6 md:mb-8 leading-relaxed">
              {hero.subtitle}
            </h2>

            <div id="form-main" className="bg-[#0b1a3d]/80 backdrop-blur-md rounded-2xl p-5 md:p-8 relative border border-white/[0.08] shadow-2xl">
              <div className="absolute -top-4 -right-4 w-24 h-24 rounded-full border border-sky-400/15" />
              <div className="absolute -bottom-6 -left-6 w-32 h-32 rounded-full border border-blue-400/10" />

              <h3 className="text-white text-lg md:text-xl font-semibold mb-2">{hero.formTitle}</h3>
              <p className="text-white/50 text-[12px] mb-5 leading-relaxed">
                Compila il modulo per richiedere una valutazione legale preliminare gratuita e riservata. Nessun costo, nessun obbligo.
              </p>

              {submitError && (
                <div className="bg-red-500/15 border border-red-500/30 rounded-lg p-3 mb-4 text-red-300 text-sm text-center">
                  {submitError}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-3 md:space-y-4" data-testid="lead-form">
                {hero.formFields.map((field) => (
                  <div key={field.name} className="relative">
                    <input
                      type={field.type}
                      name={field.name}
                      placeholder={field.placeholder}
                      value={formData[field.name] || ''}
                      onChange={handleChange}
                      onFocus={handleFocus}
                      autoComplete="off"
                      data-testid={`field-${field.name}`}
                      className={`w-full px-4 py-3 md:py-3.5 bg-[#0e2252]/70 border rounded-lg text-white text-[15px] placeholder-white/40 outline-none transition-all ${
                        errors[field.name] && touched[field.name]
                          ? 'border-red-500/60 focus:border-red-500'
                          : touched[field.name] && !errors[field.name] && formData[field.name]
                          ? 'border-green-500/60'
                          : 'border-white/[0.12] focus:border-sky-400/50 focus:shadow-[0_0_0_2px_rgba(56,189,248,0.15)]'
                      }`}
                    />
                    {touched[field.name] && !errors[field.name] && formData[field.name] && (
                      <span className="absolute right-3 top-1/2 -translate-y-1/2">
                        <Check size={18} className="text-green-400" />
                      </span>
                    )}
                    {errors[field.name] && touched[field.name] && (
                      <p className="text-red-400 text-xs mt-1 text-left" data-testid={`error-${field.name}`}>
                        {field.errorMsg}
                      </p>
                    )}
                  </div>
                ))}

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="submit-btn"
                  className="w-full py-3.5 md:py-4 bg-[#22c55e] hover:bg-[#16a34a] active:scale-[0.98] disabled:opacity-60 text-white font-bold text-base md:text-lg rounded-lg cursor-pointer border-none shadow-lg shadow-green-900/30 transition-all duration-200"
                >
                  {submitting ? 'Invio in corso...' : hero.submitText}
                </button>

                <label className="flex items-start gap-2.5 cursor-pointer mt-2">
                  <input
                    type="checkbox"
                    name="consent"
                    checked={formData.consent}
                    onChange={handleChange}
                    className="mt-0.5 w-4 h-4 accent-[#22c55e] cursor-pointer flex-shrink-0"
                  />
                  <span className="text-white/50 text-[11px] leading-relaxed text-left">
                    Ho letto e accetto l'<Link to="/privacy-policy" className="text-white/70 underline hover:text-white/90">Informativa sulla Privacy</Link> e i{' '}
                    <Link to="/terms" className="text-white/70 underline hover:text-white/90">Termini e Condizioni</Link>.
                    Acconsento al trattamento dei miei dati personali per essere ricontattato in merito alla mia richiesta di informazioni legali preliminari, ai sensi del GDPR (Reg. UE 2016/679).
                  </span>
                </label>

                {errors.consent && (
                  <p className="text-red-400 text-xs text-left">È necessario accettare l'informativa per procedere</p>
                )}
              </form>

              <p className="text-white/30 text-[10.5px] mt-3 leading-relaxed text-left">
                I dati inseriti saranno trattati con riservatezza e utilizzati esclusivamente per rispondere alla tua richiesta, ai sensi del GDPR. Nessuna condivisione con terze parti a scopo commerciale. Puoi revocare il consenso in qualsiasi momento scrivendo a <span className="text-white/50">info@lexrecupero.it</span>.
              </p>

              <div className="flex items-center justify-center gap-1.5 mt-3 text-white/30 text-[11px]">
                <Shield size={12} />
                <span>Servizio professionale riservato &mdash; Studio Legale registrato in Italia</span>
              </div>

              <div className="mt-3 pt-3 border-t border-white/10">
                <p className="text-white/20 text-[10px] leading-relaxed text-center">
                  La valutazione preliminare non costituisce consulenza legale e non crea un rapporto professionale. Ogni caso è valutato individualmente. Nessun risultato può essere garantito.
                </p>
              </div>
            </div>
          </div>

          <div className="w-full lg:w-7/12 flex items-center justify-center">
            <div
              className="w-full rounded-2xl overflow-hidden drop-shadow-2xl"
              style={{
                maxWidth: 680,
                aspectRatio: '4 / 3',
                position: 'relative',
              }}
            >
              <img
                src={hero.heroImage}
                alt="Lex Recupero™ — Studio Legale per Controversie da Investimenti Online"
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: 'center top',
                }}
                loading="eager"
                fetchpriority="high"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
